import { FieldArray, useField, useFormikContext } from 'formik';
import styled from 'styled-components';
import { Button, Menu, MenuItem } from '@mui/material';
import { AddCircleOutlineOutlined, Settings } from '@mui/icons-material';
import { FC, useCallback, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable, OnDragEndResponder } from 'react-beautiful-dnd';

import { Field as TField, FieldType } from '../../types/form-fields';
import { isTextField, TextField } from './text-field';
import { useAppDispatch } from '../../lib';
import { closeSidebar, openSidebar } from '../../store';
import { getDefaultFieldValue } from './utils';
import { isInputField, InputField } from './input-field';
import { EmailInputField, isEmailInputField } from './email-input-field';
import { isCheckboxField, CheckboxField as CheckboxFormField } from './checkbox-field';
import { isRadioGroupField, RadioGroupField } from './radio-group-field';
import { isSelectField, SelectField } from './select-field';
import { isPhoneInputField, PhoneInputField } from './phone-input-field';
import { isButtonField, ButtonField } from './button-field';
import { Sidebar } from '../sidebar';
import { WidgetSettingsSidebar } from './widget-settings-sidebar';
import { SpacerField, isSpacerField } from './spacer-field';
import { Branding } from '../../games/common/branding';

type GenericFieldProps = {
  formField: TField;
  name: string;
  remove: () => void;
};

export const GenericField: FC<GenericFieldProps> = ({ name, formField, remove }) => {
  const props = {
    key: name,
    name,
    remove,
  };

  if (isTextField(formField)) {
    return <TextField {...props} />;
  }

  if (isInputField(formField)) {
    return <InputField {...props} />;
  }

  if (isEmailInputField(formField)) {
    return <EmailInputField {...props} />;
  }

  if (isCheckboxField(formField)) {
    return <CheckboxFormField {...props} />;
  }

  if (isRadioGroupField(formField)) {
    return <RadioGroupField {...props} />;
  }

  if (isSelectField(formField)) {
    return <SelectField {...props} />;
  }

  if (isPhoneInputField(formField)) {
    return <PhoneInputField {...props} />;
  }

  if (isButtonField(formField)) {
    return <ButtonField {...props} />;
  }

  if (isSpacerField(formField)) {
    return <SpacerField {...props} />;
  }

  return null;
};

type FormEditorProps = {
  isDesktop: boolean;
};

const getPossibleFormFields = (formFields: TField[]) => {
  const hasPhoneInput = formFields.some((field) => field.type === FieldType.PhoneInput);
  const hasEmailInput = formFields.some((field) => field.type === FieldType.EmailInput);

  return Object.values(FieldType).filter((fieldType) => {
    if (fieldType === FieldType.PhoneInput && hasPhoneInput) {
      return false;
    }

    if (fieldType === FieldType.EmailInput && hasEmailInput) {
      return false;
    }

    if (fieldType === FieldType.Button) {
      return false;
    }

    return true;
  });
};

export const FormEditor: FC<FormEditorProps> = ({ isDesktop }) => {
  const [field, , helpers] = useField<{ fields: TField[] }>('campaign.formFields');
  const { submitForm } = useFormikContext();
  const dispatch = useAppDispatch();
  const addFieldButtonRef = useRef<HTMLButtonElement>(null);
  const [addFieldMenuOpen, setAddFieldMenuOpen] = useState(false);

  const closeFieldTypeMenu = useCallback(() => {
    setAddFieldMenuOpen(false);
  }, []);

  const formFields = field.value.fields;
  const fieldSliceName = 'campaign.formFields.fields';

  const possibleFieldValues = getPossibleFormFields(formFields);

  const openWidgetSettingsSidebar = useCallback(() => {
    dispatch(
      openSidebar({
        content: <WidgetSettingsSidebar />,
      })
    );
  }, [dispatch]);

  const handleFieldDragStart = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  const handleFieldDragEnd = useCallback<OnDragEndResponder>(
    (result) => {
      if (!result.destination) {
        return;
      }

      const newFieldsArray = Array.from(field.value.fields);
      const [removed] = newFieldsArray.splice(result.source.index, 1);
      newFieldsArray.splice(result.destination.index, 0, removed);

      helpers.setValue({ fields: newFieldsArray });
      submitForm();
    },
    [field.value.fields, helpers, submitForm]
  );

  return (
    <>
      <FormEditorWrapper>
        <FieldArray name={fieldSliceName}>
          {({ remove, push }) => (
            <>
              <DragDropContext onDragEnd={handleFieldDragEnd} onDragStart={handleFieldDragStart}>
                <Droppable
                  droppableId="droppable-form"
                  renderClone={(provided, _, rubric) => {
                    const index = rubric.source.index;
                    return (
                      <GenericFieldWrapper
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                      >
                        {isDesktop && <DraggableZone {...provided.dragHandleProps} />}

                        <GenericField
                          formField={formFields[index]}
                          name={`${fieldSliceName}[${index}]`}
                          remove={() => {
                            dispatch(closeSidebar());
                            remove(index);
                            submitForm();
                          }}
                        />
                      </GenericFieldWrapper>
                    );
                  }}
                >
                  {(provided) => (
                    <FormFieldsWrapper {...provided.droppableProps} ref={provided.innerRef}>
                      {formFields.map((formField, index) => (
                        <Draggable key={index} draggableId={index.toString()} index={index}>
                          {(provided) => {
                            return (
                              <GenericFieldWrapper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={provided.draggableProps.style}
                              >
                                {isDesktop && <DraggableZone {...provided.dragHandleProps} />}

                                <GenericField
                                  formField={formField}
                                  name={`${fieldSliceName}[${index}]`}
                                  remove={() => {
                                    dispatch(closeSidebar());
                                    remove(index);
                                    submitForm();
                                  }}
                                />
                              </GenericFieldWrapper>
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </FormFieldsWrapper>
                  )}
                </Droppable>
              </DragDropContext>

              <Branding formFields={formFields} />

              {isDesktop && (
                <AddFormFieldButton
                  variant="contained"
                  onClick={() => {
                    setAddFieldMenuOpen(true);
                  }}
                  ref={addFieldButtonRef}
                >
                  <AddCircleOutlineOutlined fontSize="small" />
                </AddFormFieldButton>
              )}

              <FieldTypeMenu
                anchorEl={addFieldButtonRef.current}
                open={addFieldMenuOpen}
                onClose={closeFieldTypeMenu}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                {possibleFieldValues.map((fieldType) => {
                  return (
                    <MenuItem
                      onClick={() => {
                        closeFieldTypeMenu();

                        const value = getDefaultFieldValue(fieldType, formFields);

                        if (value) {
                          push(value);
                          submitForm();
                        }
                      }}
                      key={fieldType}
                    >
                      <FieldMenuItem>{fieldType.replace('-', ' ')}</FieldMenuItem>
                    </MenuItem>
                  );
                })}
              </FieldTypeMenu>
            </>
          )}
        </FieldArray>
      </FormEditorWrapper>

      {isDesktop && (
        <SettingsButton onClick={openWidgetSettingsSidebar} variant="contained">
          <Settings fontSize="small" />
        </SettingsButton>
      )}

      <Sidebar />
    </>
  );
};

const FormEditorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 8px;
  gap: 4px;
`;

const AddFormFieldButton = styled(Button)`
  min-width: auto;
  padding: 8px;
  margin-top: 12px;
`;

const FieldMenuItem = styled.div`
  min-width: 150px;

  ::first-letter {
    text-transform: uppercase;
  }
`;

const SettingsButton = styled(Button)`
  position: absolute;
  top: 12px;
  left: 12px;
  min-width: auto;
  padding: 8px;
  z-index: 1001;
`;

const FieldTypeMenu = styled(Menu)`
  margin-top: 4px;
`;

export const DraggableZone = styled.div`
  width: 20px;
  flex-shrink: 0;
  opacity: 0;
  background-image: radial-gradient(circle at center, rgba(0, 0, 0, 0.25) 1px, transparent 0);
  background-image: radial-gradient(circle at center, rgba(255, 255, 255, 0.25) 1px, transparent 0);
  background-size: 4px 4px;
  background-repeat: round;
`;

const GenericFieldWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  cursor: pointer;

  :hover {
    box-shadow: 0px 0px 0 1px rgba(255, 255, 255, 0.25), 0px 0px 0 1px rgba(0, 0, 0, 0.25);
  }

  :hover {
    ${DraggableZone} {
      opacity: 1;
    }
  }
`;
